import { SizeAdvisorKeys } from '@/enums/frame-genius'
import { SuggestedFeatures, SuggestedSizes } from '@/types/frame-genius'
import wcs from '@/scripts/libs/wcs'
import { WcsPdpSize } from '@/scripts/interfaces/normalizer'

export class SizeAdvisor {
  private static SIZE_ADVISOR_SUGGESTED_SIZE_HASH = '#/suggested-size'

  static trackSizeAdvisorOpenEvent = (multiAnalytics: any) => {
    multiAnalytics.trackEvent({
      event: 'Click',
      value: {
        data_element_id: 'X_X_Filters_SizeGenius_Open',
      },
    })
  }

  static setSizeAdivsorPlpToggleStatus = (status: boolean) => {
    sessionStorage.setItem(SizeAdvisorKeys.PLP_TOGGLE_STATUS, `${status}`)
    localStorage.setItem(SizeAdvisorKeys.PLP_TOGGLE_STATUS, `${status}`)
  }

  static getSizeAdvisorPlpToggleStatus = (): boolean => {
    const sessionStoragePlpToggleStatus = sessionStorage.getItem(SizeAdvisorKeys.PLP_TOGGLE_STATUS)
    const localStoragePlpToggleStatus = localStorage.getItem(SizeAdvisorKeys.PLP_TOGGLE_STATUS)
    const isToggleEnabled = sessionStoragePlpToggleStatus || localStoragePlpToggleStatus
    return isToggleEnabled === 'true'
  }

  static mapSuggestedSizes = (
    sizes: WcsPdpSize[],
    suggestedFeatures: SuggestedFeatures
  ): SuggestedSizes | undefined => {
    const mappedSizes = {} as SuggestedSizes

    sizes.forEach((size: WcsPdpSize) => {
      const sizeLabel = size?.label
      const hinge = size?.hingeDistance
      mappedSizes[sizeLabel] = hinge
    })

    const feelingWornSizes = window.frameAdvisor?.SizeAdvisorWidget?.getSuggestedSizes(
      suggestedFeatures?.size?.optimalHinge,
      mappedSizes
    )
    return feelingWornSizes
  }

  private static crossSizeByFitting = (
    size: WcsPdpSize,
    suggestedSizes: SuggestedSizes,
    fitting: string
  ) => {
    if (!suggestedSizes) {
      return false
    }
    const entry = suggestedSizes[size.label]
    return !!entry && entry === fitting
  }

  static isSizeRecommended = (size: WcsPdpSize, suggestedSizes: SuggestedSizes) => {
    return this.crossSizeByFitting(size, suggestedSizes, SizeAdvisorKeys.RECOMMENDED)
  }

  static isSizeAlsoRightForYou = (size: WcsPdpSize, suggestedSizes: SuggestedSizes) => {
    return this.crossSizeByFitting(size, suggestedSizes, SizeAdvisorKeys.ALSO_RIGHT_FOR_YOU)
  }

  static isSizeTooTight = (size: WcsPdpSize, suggestedSizes: SuggestedSizes) => {
    return this.crossSizeByFitting(size, suggestedSizes, SizeAdvisorKeys.TIGHT)
  }

  static isSizeNextBestSize = (size: WcsPdpSize, suggestedSizes: SuggestedSizes) => {
    return this.crossSizeByFitting(size, suggestedSizes, SizeAdvisorKeys.NEXT_BEST_SIZE)
  }

  static isSizeTooLoose = (size: WcsPdpSize, suggestedSizes: SuggestedSizes) => {
    return this.crossSizeByFitting(size, suggestedSizes, SizeAdvisorKeys.LOOSE)
  }

  static addSizeAdvisorHashPath = () => {
    window.location.hash = SizeAdvisor.SIZE_ADVISOR_SUGGESTED_SIZE_HASH
  }

  static mapSizeAdvisorHingesFacet = (
    hingesFacet: string,
    minimumHinge: number,
    maximumHinge: number
  ): string => {
    const mappedHinges = `:[${minimumHinge} TO ${maximumHinge}]`
    return hingesFacet + mappedHinges
  }

  static isSizeAdvisorExcludedInThisPage = () => {
    const { frameGenius } = wcs.getStoreConfig()

    const pathName = `${window?.location?.pathname}`

    const routesWithoutSizeAdvisor = frameGenius?.routesWithoutSizeAdvisor?.split(',')
    return (
      !!routesWithoutSizeAdvisor?.some((route: string) => pathName.includes(route)) &&
      this.isSizeAdvisorFeatureEnabled()
    )
  }

  static isSizeAdvisorFeatureEnabled = () => {
    const { frameGenius } = wcs.getStoreConfig()
    return frameGenius.isSizeAdvisorEnabled
  }
}
